import CryptoJS from "crypto-js";
import type { JWTPayload } from "jose";
import { jwtVerify, SignJWT } from "jose";

import { createSession } from "@/utils/common/validateHelper/validateUtils";

import { jsonParse } from "../jsonHelper/jsonHelper";

export const easyEncode = (data: string): string => {
  try {
    return Buffer.from(data).toString("base64");
  } catch (error) {
    return data;
  }
};

export const easyDecode = (data: string): string => {
  try {
    return Buffer.from(data, "base64").toString("utf-8");
  } catch (error) {
    return data;
  }
};

export function base64UrlDecode(base64Url?: string): string {
  if (!base64Url) return "";
  return Buffer.from(base64Url, "base64").toString("utf8");
}

export function base64UrlEncode(data: object): string {
  return Buffer.from(JSON.stringify(data))
    .toString("base64")
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

// export const generateJWT = (payload: object) => {
//   try {
//     const privateKey = process.env.NEXT_PUBLIC_DATA_TOKEN;

//     if (!privateKey) return null;

//     const token = jwt.sign(payload, privateKey);

//     return token;
//   } catch (error) {
//     return null;
//   }
// };

export const generateJWT = async (payload: object) => {
  try {
    const privateKey = process.env.NEXT_PUBLIC_DATA_TOKEN;

    if (!privateKey) return null;

    // Encode the private key
    const secret = new TextEncoder().encode(privateKey);

    // Create the token using `SignJWT`
    const token = await new SignJWT(payload as JWTPayload)
      .setProtectedHeader({ alg: "HS256" }) // Specify the algorithm
      .sign(secret);

    return token;
  } catch (error) {
    // console.error("Error generating JWT:", error);
    return null;
  }
};

export const decodeJWT = async (token: string) => {
  try {
    const privateKey = process.env.NEXT_PUBLIC_DATA_TOKEN;

    if (!privateKey) return null;

    // Encode the private key
    const secret = new TextEncoder().encode(privateKey);

    // Verify and decode JWT
    const { payload } = await jwtVerify(token, secret);

    return payload; // Decoded user info
  } catch (error) {
    return null; // Invalid or expired token
  }
};

function getAlphabetPosition(letter: string): number {
  const lowercaseLetter: string = letter.toLowerCase();

  const asciiCodeOfA: number = "a".charCodeAt(0);

  const asciiCodeOfLetter: number = lowercaseLetter.charCodeAt(0);

  const position: number = asciiCodeOfLetter - asciiCodeOfA + 1;

  return position;
}

export function encode(input: string): string {
  const inputText = `kjasghdikaj@${input}@agjsdgajshdg`;

  const publicKey = "112787D885GS544H5";

  let publicKeySameLengthAsInput = "";

  // Repeat the publicKey until it has the same length as the input
  while (publicKeySameLengthAsInput.length < inputText.length) {
    publicKeySameLengthAsInput += publicKey;
  }

  let encoded = "";
  let indexCutting = 1;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < inputText.length; i++) {
    // Reset indexCutting if it reaches 3
    indexCutting = indexCutting >= 3 ? 1 : indexCutting + 1;

    // Extract the first digit of the publicKey
    const firstDigit = publicKeySameLengthAsInput.substring(0, 1);
    publicKeySameLengthAsInput = publicKeySameLengthAsInput.substring(1);

    // Convert the firstDigit to a number if it's a digit, otherwise get its position in the alphabet
    const plusCharCode = Number.isNaN(Number(firstDigit))
      ? getAlphabetPosition(firstDigit)
      : parseInt(firstDigit, 10);

    // Get the character code of the input character and encode it
    const charCode = inputText.charCodeAt(i);
    const newCharCode = charCode - plusCharCode;
    encoded += String.fromCharCode(newCharCode);
  }

  encoded = easyEncode(encoded);

  return encoded;
}

export function decode(input: string): string {
  const publicKey = "112787D885GS544H5";

  const inputText = easyDecode(input);

  let publicKeySameLengthAsInput = "";

  while (publicKeySameLengthAsInput.length < inputText.length) {
    publicKeySameLengthAsInput += publicKey;
  }

  let decoded = "";
  let indexCutting = 1;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < inputText.length; i++) {
    indexCutting = indexCutting >= 3 ? 1 : indexCutting + 1;

    const firstDigit = publicKeySameLengthAsInput.substring(0, 1);
    publicKeySameLengthAsInput = publicKeySameLengthAsInput.substring(1);

    const plusCharCode = Number.isNaN(Number(firstDigit))
      ? getAlphabetPosition(firstDigit)
      : Number(firstDigit);

    const charCode = inputText.charCodeAt(i);
    const newCharCode = charCode + plusCharCode;
    decoded += String.fromCharCode(newCharCode);
  }

  return decoded.replace(/^kjasghdikaj@|@agjsdgajshdg$/g, "");
}

export const encodeJson = (jsonData: object): string => {
  const jsonString = JSON.stringify(jsonData);
  return encode(jsonString);
};

export const decodeJson = (encodedString: string): object | null => {
  const decodedString = decode(encodedString);

  return jsonParse(decodedString);
};

export const apiRequestEncodeDecodePayment = (data: object): string => {
  const encodedString = encodeJson(data);
  const hashEncodedStrings = createSession(encodedString);
  const decodedJson = decodeJson(encodedString);
  const dataRequest: any = {
    q: encodedString,
    hash: hashEncodedStrings,
  };
  return JSON.stringify(dataRequest);
};

// ======================================================================
//! ! GA encode and decode Email

// Define types for encryption results
interface EncryptionResult {
  iv: string; // Initialization Vector
  encryptedData: string; // Encrypted content
}

// Define payload structure for storing encrypted email
interface EmailStoragePayload {
  email: string; // Encrypted email
  iv: string; // IV needed for decryption
  hash: string; // Hash for data integrity
}

// Utility function to encrypt an email
export function encryptEmail(
  email: string,
  secretKey: string,
): EncryptionResult {
  // Ensure the secret key is 32 bytes (256 bits) and provided as a hex string
  if (secretKey.length !== 64) {
    throw new Error(
      "Invalid secret key length. Ensure it is a 64-character hex string.",
    );
  }

  const key = CryptoJS.enc.Hex.parse(secretKey); // Parse the secret key from hex
  const iv = CryptoJS.lib.WordArray.random(16); // Generate a random IV

  const encrypted = CryptoJS.AES.encrypt(email, key, { iv });

  return {
    iv: iv.toString(CryptoJS.enc.Hex),
    encryptedData: encrypted.ciphertext.toString(CryptoJS.enc.Hex),
  };
}

// Utility function to decrypt an email
export function decryptEmail(
  encryptedData: string,
  secretKey: string,
  iv: string,
): string {
  if (secretKey.length !== 64) {
    throw new Error(
      "Invalid secret key length. Ensure it is a 64-character hex string.",
    );
  }

  const key = CryptoJS.enc.Hex.parse(secretKey); // Parse the secret key from hex
  const ivWordArray = CryptoJS.enc.Hex.parse(iv); // Parse the IV from hex

  const ciphertext = CryptoJS.enc.Hex.parse(encryptedData); // Parse the encrypted data from hex
  const cipherParams = CryptoJS.lib.CipherParams.create({ ciphertext });
  const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
    iv: ivWordArray,
  });

  return decrypted.toString(CryptoJS.enc.Utf8);
}

// Function to store encrypted email securely
export function storeEmail(
  email: string,
  secretKey: string,
): EmailStoragePayload {
  // Encrypt the email
  const { iv, encryptedData } = encryptEmail(email, secretKey);

  // Generate a hash of the encrypted email for integrity checking
  const hash = CryptoJS.SHA256(encryptedData).toString(CryptoJS.enc.Hex);

  // Return the payload containing the encrypted email, IV, and hash
  return {
    email: encryptedData,
    iv,
    hash,
  };
}

// Function to retrieve and decrypt an email
export function retrieveEmail(
  payload: EmailStoragePayload,
  secretKey: string,
): string {
  const { email, iv, hash } = payload;

  // Validate hash integrity
  const computedHash = CryptoJS.SHA256(email).toString(CryptoJS.enc.Hex);
  if (computedHash !== hash) {
    throw new Error(
      "Data integrity check failed. The email data might have been tampered with.",
    );
  }

  // Decrypt the email
  return decryptEmail(email, secretKey, iv);
}

//  Example Usage
// const secretKey = crypto.randomBytes(32).toString("hex"); // Generate a 256-bit (32-byte) secret key
// const emailToStore = "user@example.com";

//  Encrypt and store the email
// const storedPayload = storeEmail(emailToStore, secretKey);
// console.log("Stored Payload:", storedPayload);

//  Decrypt and retrieve the email
// const retrievedEmail = retrieveEmail(storedPayload, secretKey);
// console.log("Retrieved Email:", retrievedEmail);

// function generateJWT(payload: object) {
//   return jwt.sign(payload, SECRET_KEY, { expiresIn: "15m" }); // Expires in 15 minutes
// }

export const randomCode = (): string => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

  // Ensure the indices are always valid
  const getRandomLetter = (): string =>
    letters.charAt(Math.floor(Math.random() * letters.length)) || "A";

  const prefix = getRandomLetter() + getRandomLetter();
  const number = Math.floor(10000000 + Math.random() * 90000000);

  return prefix + number;
};
